import { forwardRef, ReactNode, useEffect, useImperativeHandle } from 'react';

import { ModalManagerContext, ModalManagerContextValue } from 'components/modal_manager/context';
import { useSuperState } from 'hooks';

import 'components/modal_manager/styles/ModalManager.scss';

type ModalManagerProps = {
    children?: ReactNode;
};

/**
 * A global wrapper used for tracking a stack of currently-visible Modals within the application,
 * allowing proper layering in the order they were opened
 */
export const ModalManager = forwardRef<ModalManagerContextValue, ModalManagerProps>((props, forwardedRef) => {

    const { children } = props;

    const [state, setState, resetState] = useSuperState({
        modalStack: [] as string[]
    });

    const setModalStack = (modalStack: string[]) => new Promise<void>(resolve => setState({ modalStack }, () => resolve()));

    // Returns the current modalStack array from state or an empty array
    const getStack = () => Array.isArray(state.modalStack) ? state.modalStack : [];

    // Finds the index (stack depth) of a specified modal (-1 if not in stack)
    const getHeightInStack = (modal: string) => getStack().indexOf(modal);

    // Returns the length of the current modalStack array
    const getStackSize = () => getStack().length;

    // Checks if the specified modal exists within the stack
    const isInStack = (modal: string) => getStack().includes(modal);

    // Checks if the specified modal is at the bottom of the stack (first item in array)
    const isBottomOfStack = (modal: string) => {

        if (!modal) return false;

        const modalStack = getStack();
        return modalStack[0] === modal;

    };

    // Checks if the specified modal is at the top of the stack (last item in array)
    const isTopOfStack = (modal: string) => {

        if (!modal) return false;

        const modalStack = getStack();
        return modalStack[modalStack.length - 1] === modal;

    };

    // Appends a given modal reference to the top of the stack
    const addToStack = async (modal: string) => {

        // If the modal already exists in the stack, do nothing
        if (isInStack(modal)) return;

        const updated_stack = [...getStack(), modal];
        await setModalStack(updated_stack);

    };

    // Explicitly removes a given modal reference from the stack, regardless of depth
    const removeFromStack = async (modal: string) => {

        // If the modal doesn't exist in the stack, do nothing
        if (!isInStack(modal)) return;

        const updated_stack = getStack().filter(entry => entry !== modal);
        await setModalStack(updated_stack);

    };

    // Removes the last modal reference (top of stack) and returns it
    const popStack = async () => {

        const modalStack = [...getStack()];
        const popped = modalStack.pop();

        await setModalStack(modalStack);

        return popped;

    };

    // Removes the first modal reference (bottom of stack) and returns it
    const shiftStack = async () => {

        const new_stack = [...getStack()];
        const shifted = new_stack.shift();

        await setModalStack(new_stack);

        return shifted;

    };

    // Resets the stack to its initial (empty) state
    const clearStack = () => setModalStack([]);

    // resetState is stable (never changes identity); empty deps intentionally runs cleanup on unmount only.
    useEffect(() => resetState, []); // eslint-disable-line react-hooks/exhaustive-deps

    const context: ModalManagerContextValue = {
        addToStack,
        clearStack,
        getHeightInStack,
        getStack,
        getStackSize,
        isBottomOfStack,
        isInStack,
        isTopOfStack,
        modalStack: state.modalStack,
        popStack,
        removeFromStack,
        shiftStack
    };

    useImperativeHandle(forwardedRef, () => ({ ...context }));

    return (
        <ModalManagerContext.Provider value={{ ...context }}>
            {children}
        </ModalManagerContext.Provider>
    );

});

ModalManager.displayName = 'ModalManager';